'use client';

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useLayoutEffect, useRef } from 'react';
import { Main } from '../Layout/entry.js';
import { cn } from '../utils/entry.js';
import ModalContent from './ModalContent.js';
import ModalFooter from './ModalFooter.js';
import ModalHeader from './ModalHeader.js';
import { useModalManager } from './ModalManagerProvider.js';
/**
 * Modal like component that should be used in cases where user interaction is required and be rendered as a full page component, eg. statistic pages in a small device / mobile view.
 *
 * The component should be used in a "full page" context, and should not be used in a "popup" context.
 * This component should be used along with {@link ModalSidekick}, which keeps the
 * background mounted and prevents interaction while the modal is active.
 *
 * Provide exactly one of `aria-label` or `aria-labelledby`
 * to give the dialog an accessible name.
 *
 * @param props Component props.
 * @param props.children The content inside the modal.
 * @param props.header The sticky header content.
 * @param props.footer he sticky footer content.
 * @param props.className Additional content container classes.
 * @returns Modal component.
 */
export default function Modal({
  children,
  header,
  footer,
  className,
  ...ariaProps
}) {
  const {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy
  } = ariaProps;
  const {
    registerModalDialog
  } = useModalManager();
  const dialogElement = useRef(undefined);
  const unregisterDialog = useRef(() => undefined);
  const dialogRef = useCallback(dialog => {
    unregisterDialog.current();
    unregisterDialog.current = () => undefined;
    dialogElement.current = dialog ?? undefined;
    if (dialog) unregisterDialog.current = registerModalDialog(dialog);
  }, [registerModalDialog]);
  useLayoutEffect(() => {
    dialogElement.current?.focus({
      preventScroll: true
    });
  }, []);
  return _jsxs("div", {
    ref: dialogRef,
    className: "bg-surface-standard relative left-1/2 -ml-[50vw] flex w-screen flex-col",
    role: "dialog",
    "aria-modal": "true",
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    tabIndex: -1,
    children: [_jsxs("div", {
      className: "safe-area-inset mx-auto w-full max-w-[1440px]",
      children: [header && _jsx(ModalHeader, {
        className: "shrink-0",
        children: header
      }), _jsx(ModalContent, {
        className: cn('pb-24', className),
        children: _jsx(Main, {
          children: children
        })
      })]
    }), footer && _jsx(ModalFooter, {
      className: "bg-surface-standard z-header-low fixed inset-x-0 bottom-0 w-full px-4 pt-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] shadow-2xl",
      children: footer
    })]
  });
}